import { useEffect, useState } from 'react'
import { ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getMemberFullName } from '@chillout/core'
import { BottomSheet, DragHandle } from '@chillout/ui'
import type { CashierMemberProfile } from '../home/useMemberSearch'
import { ChangePinModal } from '../changePin/ChangePinModal'
import { MemberInfoHeader } from './MemberInfoHeader'
import { StampCollectionSection } from './StampCollectionSection'
import { MemberStampDataSection } from './MemberStampDataSection'
import { StampSuccessView } from './StampSuccessView'
import type { StampTransactionResult } from './StampSuccessView'
import { useStampSettings } from './useStampSettings'
import { useMemberStampData } from './useMemberStampData'
import { useAwardStamps, InvalidCashierPinError } from './useAwardStamps'
import { useRemoveStamps, InvalidManagerPinError as InvalidManagerPinErrorCorrection } from '../stampCorrection/useRemoveStamps'
import { StampCorrectionSheet } from '../stampCorrection/StampCorrectionSheet'
import { StampCorrectionSuccessView } from '../stampCorrection/StampCorrectionSuccessView'
import type { StampCorrectionResult } from '../stampCorrection/StampCorrectionSuccessView'
import { RewardRestoreSheet } from '../rewardRestore/RewardRestoreSheet'
import { RewardRestoreSuccessView } from '../rewardRestore/RewardRestoreSuccessView'
import type { RewardRestoreResult } from '../rewardRestore/RewardRestoreSuccessView'
import { useRestoreReward, InvalidManagerPinError } from '../rewardRestore/useRestoreReward'
import { useCashierFeatureFlags } from './useCashierFeatureFlags'

interface StampAwardingSheetProps {
  member: CashierMemberProfile | null
  onClose: () => void
  onScanNext?: () => void
}

export function StampAwardingSheet({ member, onClose, onScanNext }: StampAwardingSheetProps) {
  const { t } = useTranslation()
  const { height } = useWindowDimensions()
  const queryClient = useQueryClient()
  const { data: settings, isLoading } = useStampSettings()
  const { data: stampData } = useMemberStampData(member?.id)
  const { data: featureFlags } = useCashierFeatureFlags()
  const { mutate: awardStamps, isPending: isAwarding } = useAwardStamps()
  const { mutate: removeStamps, isPending: isRemoving } = useRemoveStamps()
  const { mutate: restoreReward, isPending: isRestoring } = useRestoreReward()

  const stampCorrectionEnabled = featureFlags?.stampCorrectionEnabled ?? true
  const rewardRestoreEnabled = featureFlags?.rewardRestoreEnabled ?? true
  const hasMenuItems = stampCorrectionEnabled || rewardRestoreEnabled

  const [mealsCount, setMealsCount] = useState(1)
  const [purchaseAmount, setPurchaseAmount] = useState('')
  const [receiptReference, setReceiptReference] = useState('')
  const [staffPinVisible, setStaffPinVisible] = useState(false)
  const [stampResult, setStampResult] = useState<StampTransactionResult | null>(null)
  const [pinError, setPinError] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [correctionVisible, setCorrectionVisible] = useState(false)
  const [managerPinVisible, setManagerPinVisible] = useState(false)
  const [correctionParams, setCorrectionParams] = useState<{ removalCount: number; reason: string; note: string } | null>(null)
  const [managerPinError, setManagerPinError] = useState<string | null>(null)
  const [correctionResult, setCorrectionResult] = useState<StampCorrectionResult | null>(null)
  const [restoreRewardVisible, setRestoreRewardVisible] = useState(false)
  const [restorePinVisible, setRestorePinVisible] = useState(false)
  const [restoreParams, setRestoreParams] = useState<{
    rewardItemId: string
    rewardName: string
    restoreCount: number
    reason: string
    note: string
  } | null>(null)
  const [restorePinError, setRestorePinError] = useState<string | null>(null)
  const [restoreResult, setRestoreResult] = useState<RewardRestoreResult | null>(null)

  useEffect(() => {
    if (member) {
      void queryClient.invalidateQueries({ queryKey: ['stamp-settings'] })
      void queryClient.invalidateQueries({ queryKey: ['cashier-feature-flags'] })
    }
    setMealsCount(1)
    setPurchaseAmount('')
    setReceiptReference('')
    setStampResult(null)
    setStaffPinVisible(false)
    setPinError(null)
    setMenuOpen(false)
    setCorrectionVisible(false)
    setManagerPinVisible(false)
    setCorrectionParams(null)
    setManagerPinError(null)
    setCorrectionResult(null)
    setRestoreRewardVisible(false)
    setRestorePinVisible(false)
    setRestoreParams(null)
    setRestorePinError(null)
    setRestoreResult(null)
  }, [member, queryClient])

  const handleAddStamps = () => {
    setPinError(null)
    setStaffPinVisible(true)
  }

  const handlePinConfirm = (pin: string) => {
    if (!settings || !member) return

    awardStamps(
      {
        memberId:         member.id,
        pin,

        purchaseAmount:   settings.earningRule.method === 'spend_based'
                            ? parseInt(purchaseAmount, 10)
                            : undefined,
        mealsCount:       settings.earningRule.method === 'per_meal'
                            ? mealsCount
                            : undefined,
        receiptReference: receiptReference || undefined,
      },
      {
        onSuccess: (result) => {
          setStaffPinVisible(false)
          setStampResult({
            stampsAdded:     result.stampsAdded,
            memberFullName:  getMemberFullName(member) ?? '',
            memberFirstName: member.firstName ?? '',
            newTotal:        result.newStampTotal,
            stampTarget:     result.stampTarget,
            rewardsUnlocked: result.rewardsUnlocked,
            rewardName:      result.rewardName,
          })
        },
        onError: (error) => {
          if (error instanceof InvalidCashierPinError) {
            setPinError(t('stampAwarding.staffPin.incorrectPin'))
          } else {
            setPinError(t('stampAwarding.staffPin.awardError'))
          }
        },
      },
    )
  }

  const handleRequestPin = (removalCount: number, reason: string, note: string) => {
    setManagerPinError(null)
    setCorrectionParams({ removalCount, reason, note })
    setManagerPinVisible(true)
  }

  const handleManagerPinConfirm = (pin: string) => {
    if (!correctionParams || !member) return
    setManagerPinError(null)

    removeStamps(
      {
        memberId:     member.id,
        pin,
        removalCount: Math.abs(correctionParams.removalCount),
        reason:       correctionParams.reason,
        note:         correctionParams.note,
      },
      {
        onSuccess: (result) => {
          setManagerPinVisible(false)
          setCorrectionResult({
            stampsRemoved:   Math.abs(correctionParams.removalCount),
            memberFullName:  [member.firstName, member.lastName].filter(Boolean).join(' '),
            memberFirstName: member.firstName ?? '',
            newTotal:        result.newStampTotal,
            stampTarget:     result.stampTarget,
          })
        },
        onError: (error) => {
          if (error instanceof InvalidManagerPinErrorCorrection) {
            setManagerPinError(t('stampCorrection.managerPin.incorrectPin'))
          } else {
            setManagerPinError(t('stampCorrection.managerPin.removeError'))
          }
        },
      },
    )
  }

  const handleRequestRestorePin = (
    rewardItemId: string,
    rewardName: string,
    restoreCount: number,
    reason: string,
    note: string,
  ) => {
    setRestorePinError(null)
    setRestoreParams({ rewardItemId, rewardName, restoreCount, reason, note })
    setRestorePinVisible(true)
  }

  const handleRestorePinConfirm = (pin: string) => {
    if (!restoreParams || !member) return
    setRestorePinError(null)

    restoreReward(
      {
        memberId:     member.id,
        pin,
        rewardItemId: restoreParams.rewardItemId,
        restoreCount: restoreParams.restoreCount,
        reason:       restoreParams.reason,
        note:         restoreParams.note,
      },
      {
        onSuccess: (result) => {
          setRestorePinVisible(false)
          setRestoreResult({
            restoreCount:    restoreParams.restoreCount,
            rewardName:      result.rewardName,
            memberFullName:  [member.firstName, member.lastName].filter(Boolean).join(' '),
            memberFirstName: member.firstName ?? '',
            newCount:        result.newCount,
          })
        },
        onError: (error) => {
          if (error instanceof InvalidManagerPinError) {
            setRestorePinError(t('rewardRestore.managerPin.incorrectPin'))
          } else {
            setRestorePinError(t('rewardRestore.managerPin.restoreError'))
          }
        },
      },
    )
  }

  const handleDone = () => {
    setStampResult(null)
    onClose()
  }

  const handleScanNext = () => {
    setStampResult(null)
    if (onScanNext) onScanNext()
    else onClose()
  }

  return (
    <BottomSheet
      visible={member !== null}
      onClose={onClose}
      backdropAccessibilityLabel={t('stampAwarding.sheetBackdropAccessibilityLabel')}
    >
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View className="relative">
          <View
            style={{ maxHeight: height * 0.80 }}
            className="rounded-t-2xl bg-canvas-card overflow-hidden"
          >
            <DragHandle />

            {stampResult ? (
              <StampSuccessView
                result={stampResult}
                onScanNext={handleScanNext}
                onDone={handleDone}
              />
            ) : restoreResult ? (
              <RewardRestoreSuccessView
                result={restoreResult}
                onScanNext={handleScanNext}
                onDone={handleDone}
              />
            ) : correctionResult ? (
              <StampCorrectionSuccessView
                result={correctionResult}
                onScanNext={handleScanNext}
                onDone={handleDone}
              />
            ) : correctionVisible && member ? (
              <StampCorrectionSheet
                member={member}
                onClose={() => setCorrectionVisible(false)}
                onRequestPin={handleRequestPin}
              />
            ) : restoreRewardVisible && member ? (
              <RewardRestoreSheet
                member={member}
                onClose={() => setRestoreRewardVisible(false)}
                onRequestPin={handleRequestRestorePin}
              />
            ) : (
              <ScrollView
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={{ paddingBottom: 40 }}
              >
                {member && <MemberInfoHeader member={member} onMenuPress={hasMenuItems ? () => setMenuOpen(true) : undefined} />}

                {isLoading && <ActivityIndicator className="mt-6" />}

                {settings && (
                  <View className="px-6">
                    <StampCollectionSection
                      settings={settings}
                      currentStamps={stampData?.currentStamps}
                      mealsCount={mealsCount}
                      onMealsCountChange={setMealsCount}
                      purchaseAmount={purchaseAmount}
                      onPurchaseAmountChange={setPurchaseAmount}
                      receiptReference={receiptReference}
                      onReceiptReferenceChange={setReceiptReference}
                      onAddStamps={handleAddStamps}
                    />
                  </View>
                )}

                {stampData && (
                  <View className="px-6 pt-6">
                    <MemberStampDataSection stampData={stampData} />
                  </View>
                )}
              </ScrollView>
            )}
          </View>

          {menuOpen && (
            <>
              <Pressable
                style={StyleSheet.absoluteFillObject}
                onPress={() => setMenuOpen(false)}
                accessibilityLabel={t('stampCorrection.menu.closeAccessibilityLabel')}
              />
              <View
                className="absolute right-6 top-10 min-w-[180px] rounded-lg border border-border bg-canvas-card py-1.5"
                style={{ zIndex: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.08, shadowRadius: 12, elevation: 8 }}
              >
                {stampCorrectionEnabled && (
                  <Pressable
                    accessibilityRole="menuitem"
                    accessibilityLabel={t('stampCorrection.menu.removeStamp')}
                    disabled={!stampData?.currentStamps}
                    className="px-4 py-3 active:bg-canvas-primary"
                    onPress={() => { setMenuOpen(false); setCorrectionVisible(true) }}
                  >
                    <Text className={`text-base ${stampData?.currentStamps ? 'text-primary' : 'text-muted'}`}>{t('stampCorrection.menu.removeStamp')}</Text>
                  </Pressable>
                )}
                {rewardRestoreEnabled && (
                  <Pressable
                    accessibilityRole="menuitem"
                    accessibilityLabel={t('stampCorrection.menu.restoreReward')}
                    className="px-4 py-3 active:bg-canvas-primary"
                    onPress={() => { setMenuOpen(false); setRestoreRewardVisible(true) }}
                  >
                    <Text className="text-base text-primary">{t('stampCorrection.menu.restoreReward')}</Text>
                  </Pressable>
                )}
              </View>
            </>
          )}
        </View>
      </KeyboardAvoidingView>

      <ChangePinModal
        mode="entry"
        autoConfirm
        visible={staffPinVisible}
        isLoading={isAwarding}
        title={t('stampAwarding.staffPin.title')}
        subtitle={t('stampAwarding.staffPin.subtitle')}
        modalAccessibilityLabel={t('stampAwarding.staffPin.modalAccessibilityLabel')}
        closeAccessibilityLabel={t('stampAwarding.staffPin.closeAccessibilityLabel')}
        errorMessage={pinError}
        getCodeAccessibilityLabel={(entered, total) =>
          t('stampAwarding.staffPin.codeProgressAccessibilityLabel', { entered, total })}
        onInputChange={() => setPinError(null)}
        onClose={() => setStaffPinVisible(false)}
        onConfirm={handlePinConfirm}
      />

      <ChangePinModal
        mode="entry"
        autoConfirm
        visible={managerPinVisible}
        isLoading={isRemoving}
        title={t('stampCorrection.managerPin.title')}
        subtitle={t('stampCorrection.managerPin.subtitle')}
        modalAccessibilityLabel={t('stampCorrection.managerPin.modalAccessibilityLabel')}
        closeAccessibilityLabel={t('stampCorrection.managerPin.closeAccessibilityLabel')}
        errorMessage={managerPinError}
        getCodeAccessibilityLabel={(entered, total) =>
          t('stampCorrection.managerPin.codeProgressAccessibilityLabel', { entered, total })}
        onInputChange={() => setManagerPinError(null)}
        onClose={() => setManagerPinVisible(false)}
        onConfirm={handleManagerPinConfirm}
      />

      <ChangePinModal
        mode="entry"
        autoConfirm
        visible={restorePinVisible}
        isLoading={isRestoring}
        title={t('rewardRestore.managerPin.title')}
        subtitle={t('rewardRestore.managerPin.subtitle')}
        modalAccessibilityLabel={t('rewardRestore.managerPin.modalAccessibilityLabel')}
        closeAccessibilityLabel={t('rewardRestore.managerPin.closeAccessibilityLabel')}
        errorMessage={restorePinError}
        getCodeAccessibilityLabel={(entered, total) =>
          t('rewardRestore.managerPin.codeProgressAccessibilityLabel', { entered, total })}
        onInputChange={() => setRestorePinError(null)}
        onClose={() => setRestorePinVisible(false)}
        onConfirm={handleRestorePinConfirm}
      />
    </BottomSheet>
  )
}